import {MagnifyingGlassIcon, XMarkIcon} from '@heroicons/react/24/solid';
import {ComboboxInput} from '@reach/combobox';
import {AnimatePresence, motion} from 'framer-motion';
import React, {ChangeEventHandler, FunctionComponent} from 'react';
import {useToggle} from 'react-use';

type SearchBarStyle = {
  backgroundColor: string;
  textColor: string;
  ringColorInactive: string;
  ringColorActive: string;
  iconColor: string;
};
type SearchBarStyleVariant = 'light' | 'dark';
const searchBarStyles: Record<SearchBarStyleVariant, SearchBarStyle> = {
  light: {
    backgroundColor: 'bg-graybluedark2',
    textColor: 'text-white',
    ringColorInactive: 'border-white',
    ringColorActive: 'border-deepblue',
    iconColor: 'fill-deepblue stroke-deepblue',
  },
  dark: {
    backgroundColor: 'bg-graybluedark2',
    textColor: 'text-white',
    ringColorInactive: 'border-graybluedark2',
    ringColorActive: 'border-deepblue',
    iconColor: 'fill-deepblue stroke-deepblue',
  },
};

export const ComboboxCustomSearchInput: FunctionComponent<{
  value: string;
  onChange: (newValue: string) => void;
  autoFocus?: boolean;
  placeholder: string;
  variant: SearchBarStyleVariant;
  style?: SearchBarStyle;
}> = ({value, onChange, autoFocus = false, placeholder, variant, style}) => {
  // External hooks

  // Internal hooks
  const [isInputFocused, toggleIsInputFocused] = useToggle(false);

  const shouldHideSearchIcon = isInputFocused && !autoFocus;

  // Functions
  const onInputChange: ChangeEventHandler<HTMLInputElement> = event => {
    onChange(event.target.value);
  };
  const resetInput = () => {
    onChange('');
  };

  const onBlur = (e: React.FocusEvent<HTMLInputElement, Element>) => {
    toggleIsInputFocused();
  };

  const {backgroundColor, textColor, ringColorInactive, ringColorActive, iconColor} = style ?? searchBarStyles[variant];

  return (
    <div
      className={`h-full flex ${backgroundColor} ${textColor} text-base outline-none ${
        shouldHideSearchIcon ? ringColorActive : ringColorInactive
      } transition-all duration-200 overflow-hidden-clip-path`}
    >
      <div className={'h-full aspect-square flex-center m-0  peer-focus:opacity-0'}>
        <MagnifyingGlassIcon className={`w-4 h-4 ${iconColor}`} />
      </div>

      <ComboboxInput
        autoFocus={autoFocus}
        type='search'
        className='grow outline-none peer bg-transparent placeholder:text-deepblue border-none'
        placeholder={placeholder}
        value={value}
        onChange={onInputChange}
        onFocus={toggleIsInputFocused}
        onBlur={onBlur}
      />

      <AnimatePresence>
        <motion.button
          initial={{opacity: 0}}
          animate={shouldHideSearchIcon && value.length > 0 ? {opacity: 1} : {opacity: 0}}
          exit={{opacity: 0}}
          transition={{ease: 'easeInOut', duration: 0.1}}
          className={'h-full aspect-square flex-center m-0 group'}
          onClick={resetInput}
        >
          <XMarkIcon className={`w-4 h-4 ${iconColor} group-hover:opacity-70`} />
        </motion.button>
      </AnimatePresence>
    </div>
  );
};
